import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImagePlaceholder } from "@/components/ui/image-placeholder";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import type { RestaurantWithEventSpaces } from "@/types/restaurant";
import { useIsMobile } from "@/hooks/use-mobile";
import { getOptimizedImageUrl, getImageSrcSet } from "@/utils/imageOptimization";

interface FeaturedRestaurantCardProps {
  restaurant: RestaurantWithEventSpaces;
}

export const FeaturedRestaurantCard = ({ restaurant }: FeaturedRestaurantCardProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const { toggleFavorite, isFavorited } = useFavorites();
  const [hasError, setHasError] = useState(false);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!session) {
      navigate('/auth');
      return;
    }
    toggleFavorite(restaurant.id);
  };

  const maxCapacity = restaurant.eventspaces?.length 
    ? Math.max(0, ...restaurant.eventspaces.map(s => parseInt(s.capacity || '0') || 0))
    : null;

  return (
    <div
      className="cursor-pointer group relative rounded-xl overflow-hidden"
      onClick={() => navigate(`/restaurant/${restaurant.id}`)}
    >
      {/* Image */}
      <div className={`relative ${isMobile ? 'h-48' : 'h-56'} overflow-hidden`}>
        <Button
          variant="ghost"
          size="icon"
          className="favorite-button absolute top-3 right-3 z-10 bg-black/30 hover:bg-black/50 backdrop-blur-sm h-9 w-9 rounded-full border-0"
          onClick={handleFavoriteClick}
        >
          <Heart 
            className={`h-4 w-4 ${isFavorited(restaurant.id) ? 'fill-red-400 text-red-400' : 'text-white'}`}
          />
        </Button>
        {!hasError && restaurant.photos?.[0] ? (
          <img
            src={getOptimizedImageUrl(restaurant.photos[0], 500)}
            srcSet={getImageSrcSet(restaurant.photos[0], [300, 500, 800]) || undefined}
            alt={restaurant.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={() => setHasError(true)}
            sizes="(max-width: 640px) 300px, 380px"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary via-primary/85 to-secondary/60">
            <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'url(/pattern.png)', backgroundRepeat: 'repeat' }} />
          </div>
        )}
        {/* Gradient overlay with info */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className={`font-display font-semibold text-white ${isMobile ? 'text-base' : 'text-lg'} leading-tight`}>
            {restaurant.name}
          </h3>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="flex items-center gap-1 text-white/70 text-xs">
              <MapPin className="h-3 w-3" />
              {restaurant.neighborhood?.name || 'NYC'}
            </span>
            {maxCapacity && (
              <span className="flex items-center gap-1 text-white/70 text-xs">
                <Users className="h-3 w-3" />
                Up to {maxCapacity}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
